import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Button from '../../components/Button'
import Icon from '../../components/Icon'
import { useStudentsStore } from '../../../stores/studentsStore'
import { useSchoolStore } from '../../../stores/schoolsStore'
import { useClassStore } from '../../../stores/classesStore'
import { useAuth } from '../../../contexts/AuthContext'
import {
  Race,
  Gender,
  Grade,
  Class,
  CreateStudentRequest,
} from '../../../types'
import StudentGeneralTab from './StudentGeneralTab'

const StudentGeneralPage: React.FC = () => {
  const { studentId, id } = useParams<{ studentId?: string; id?: string }>()
  const paramId = studentId || id
  const navigate = useNavigate()
  const { user } = useAuth()
  const {
    students,
    loading,
    error,
    isCreating,
    isUpdating,
    fetchStudents,
    addStudent,
    editStudent,
    removeStudent,
    clearError,
  } = useStudentsStore()

  const { schools, loading: schoolsLoading, fetchSchools } = useSchoolStore()
  const { schoolClasses, fetchSchoolClasses, updateStudentClasses } =
    useClassStore()

  const [formData, setFormData] = useState({
    studentId: '',
    firstName: '',
    lastName: '',
    grade: '',
    race: '',
    gender: '',
    caseManager: '',
  })
  const [selectedSchool, setSelectedSchool] = useState('')
  const [selectedClasses, setSelectedClasses] = useState<string[]>([])

  const isNewStudent = paramId === 'new'
  const currentStudent = !isNewStudent
    ? students.find((s) => s._id === paramId)
    : null

  useEffect(() => {
    if (students.length === 0) {
      fetchStudents()
    }
    if (schools.length === 0) {
      fetchSchools()
    }
    if (schoolClasses.length === 0) {
      fetchSchoolClasses()
    }
  }, [
    students.length,
    schools.length,
    schoolClasses.length,
    fetchStudents,
    fetchSchools,
    fetchSchoolClasses,
  ])

  useEffect(() => {
    if (!isNewStudent && currentStudent) {
      setFormData({
        studentId: currentStudent.studentId || '',
        firstName: currentStudent.firstName,
        lastName: currentStudent.lastName,
        grade: currentStudent.grade,
        race: currentStudent.race || '',
        gender: currentStudent.gender || '',
        caseManager: currentStudent.caseManager || '',
      })

      const schoolId =
        typeof currentStudent.school === 'object'
          ? currentStudent.school._id
          : currentStudent.school
      setSelectedSchool(schoolId)

      const studentClasses = schoolClasses
        .filter((cls: Class) => {
          return cls.students.some((student) => {
            const studentId =
              typeof student === 'object' ? student._id : student
            return studentId === currentStudent._id
          })
        })
        .map((cls: Class) => cls._id)

      setSelectedClasses(studentClasses)
    }
  }, [isNewStudent, currentStudent, schoolClasses])

  useEffect(() => {
    return () => {
      clearError()
    }
  }, [clearError])

  const handleSaveStudent = async () => {
    const organizationId =
      typeof user?.organization === 'string'
        ? user.organization
        : user?.organization._id

    if (
      !formData.firstName.trim() ||
      !formData.lastName.trim() ||
      !formData.grade.trim() ||
      !selectedSchool ||
      !organizationId
    ) {
      return
    }

    try {
      const studentData: CreateStudentRequest = {
        studentId: formData.studentId.trim() || undefined,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        grade: formData.grade as Grade,
        race: formData.race ? (formData.race as Race) : undefined,
        gender: formData.gender ? (formData.gender as Gender) : undefined,
        caseManager: formData.caseManager.trim() || undefined,
        school: selectedSchool,
        accommodations: currentStudent?.accommodations || [],
        organization: organizationId,
      }

      let savedStudentId: string

      if (isNewStudent) {
        const newStudent = await addStudent(studentData)
        savedStudentId = newStudent._id
      } else if (paramId) {
        await editStudent(paramId, studentData)
        savedStudentId = paramId
      } else {
        return
      }

      await updateStudentClasses(savedStudentId, selectedClasses)
      navigate('/manage/students')
    } catch (err) {
      console.error(
        isNewStudent ? 'Error creating student:' : 'Error updating student:',
        err,
      )
    }
  }

  const handleDeleteStudent = async () => {
    if (!currentStudent) return

    if (
      window.confirm(
        `Are you sure you want to delete "${currentStudent.firstName} ${currentStudent.lastName}"? This action cannot be undone.`,
      )
    ) {
      try {
        await removeStudent(currentStudent._id)
        navigate('/manage/students')
      } catch (err) {
        console.error('Error deleting student:', err)
      }
    }
  }

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleClassChange = (selectedIds: string[]) => {
    setSelectedClasses(selectedIds)
  }

  const handleSchoolChange = (schoolId: string) => {
    setSelectedSchool(schoolId)
    setSelectedClasses([])
  }

  if (
    (loading && students.length === 0) ||
    (schoolsLoading && schools.length === 0)
  ) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (!isNewStudent && !currentStudent && !loading) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          Student not found
        </div>
        <Button
          onClick={() => navigate('/manage/students')}
          variant="link"
          className="mt-4 flex items-center gap-2"
        >
          <Icon type="arrow-left" className="w-5 h-5" />
          Back to Students
        </Button>
      </div>
    )
  }

  return (
    <div className="mx-auto space-y-6">
      <div>
        <Button
          onClick={() => navigate('/manage/students')}
          variant="link"
          className="mb-4 flex items-center gap-2"
        >
          <Icon type="arrow-left" className="w-5 h-5" />
          Back to Students
        </Button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isNewStudent ? 'Add New Student' : 'Edit Student'}
        </h1>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() =>
                navigate(
                  isNewStudent
                    ? '/manage/students'
                    : `/manage/students/${paramId}/general`,
                )
              }
              className="px-6 py-3 text-sm font-medium border-b-2 border-blue-600 text-blue-600"
            >
              General
            </button>
            {!isNewStudent && (
              <>
                <button
                  onClick={() =>
                    navigate(`/manage/students/${paramId}/accommodations`)
                  }
                  className="px-6 py-3 text-sm font-medium border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 transition-colors"
                >
                  Accommodations
                </button>
                <button
                  onClick={() => navigate(`/manage/students/${paramId}/goals`)}
                  className="px-6 py-3 text-sm font-medium border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 transition-colors"
                >
                  Goals
                </button>
              </>
            )}
          </nav>
        </div>

        <div className="p-6">
          <StudentGeneralTab
            formData={formData}
            handleInputChange={handleInputChange}
            selectedSchool={selectedSchool}
            handleSchoolChange={handleSchoolChange}
            selectedClasses={selectedClasses}
            handleClassChange={handleClassChange}
            schools={schools}
            schoolClasses={schoolClasses}
          />

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm mt-6">
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-between items-center px-6 py-4 border-t bg-gray-50 rounded-b-lg">
          <div>
            {!isNewStudent && (
              <Button
                onClick={handleDeleteStudent}
                disabled={isUpdating}
                variant="link"
                className="px-4 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg"
              >
                Delete Student
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => navigate('/manage/students')}
              disabled={isCreating || isUpdating}
              variant="secondary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveStudent}
              disabled={
                isCreating ||
                isUpdating ||
                !formData.firstName.trim() ||
                !formData.lastName.trim() ||
                !formData.grade.trim() ||
                !selectedSchool
              }
              variant="primary"
              loading={isCreating || isUpdating}
            >
              {isNewStudent ? 'Add Student' : 'Update Student'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default StudentGeneralPage
